import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import Constants from '@/constants/const';
import useLazy from '@/hooks/useLazy';
import { hasFtuFlags, updatePopupCreateAccountSession } from '@/utils/ftu';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import useSourceOfStudent from '@/hooks/source-of-student';
import { getPageEventName } from '@/utils/common/pageKey';
import { getPathNoLangOnClient } from '@/utils/url';
import { isLatinoChannel } from '@/utils/common/latino-app';
import { isMobile } from '@/utils/common/device';
import { getCookie, setCookie } from '@/utils/cookie';

const DynamicPopupCreateAccount = dynamic(() => import('@/components/homepage/components/PopupCreateAccount'), {
  ssr: false
});

const DynamicStudentLogin = dynamic(
  () => import('@/components/homepage/components/PopupCreateAccount/StudentLogin/student-login-poup'),
  {
    ssr: false
  }
);

type Props = {
  showPopupCreateAccount?: boolean;
  organic?: string;
  preload?: boolean;
} & ObjectType;

const DynamicCreateAccount = forwardRef((props: Props, ref) => {
  const router = useRouter();
  const { query, asPath } = router;
  const { visible, show, hide } = useLazy();
  const { organic, ...rest } = props;

  const [hasShowStore, setHasShowStore] = useState(false);

  const { checkSourceOfStudent } = useSourceOfStudent();

  const cancelledRef = useRef(false);

  const checkPopupVisible = () => {
    // 在ftu展示之前显示了其他popup，或者跳转其他页面，ftu显示取消
    const popupIds = ['first_time_use_popup', 'student_first_time_use_popup', 'ftu_store_zip_popup'];
    const otherSelector = popupIds.map(id => `:not(#${id})`).join('');
    const otherPopupVisible = !!document?.querySelector(`[data-popup-visible="true"]${otherSelector}`);
    if (otherPopupVisible || cancelledRef.current) return;
    show();
    updatePopupCreateAccountSession();
    const time = new Date().getTime() / 1000;
    const count = +(getCookie(Constants.FTU_SHOW_COUNT) || '0');
    setCookie(Constants.FTU_SHOW_TIME, `${time}`);
    setCookie(Constants.FTU_SHOW_COUNT, `${count + 1}`);
  };

  const handlePopupShow = (triggerType = 'onload', duration = 3000) => {
    if (isLatinoChannel() && !isMobile()) return;
    if (triggerType === 'onclick' || triggerType === 'storeSelect') {
      show();
      updatePopupCreateAccountSession();
      if (triggerType === 'storeSelect') {
        setHasShowStore(true);
      } else {
        // const time = new Date().getTime() / 1000;
        // const count = +(getCookie(Constants.FTU_SHOW_COUNT) || '0');
        // setCookie(Constants.FTU_SHOW_COUNT, `${count + 1}`);
        // setCookie(Constants.FTU_SHOW_TIME, `${time}`);
      }
      return;
    }
    if (triggerType === 'storeExsit') {
      setHasShowStore(true);
    }
    const withFlags = !!query?.ftuflags && hasFtuFlags(query?.ftuflags, Constants.FTU_FLAGS.DELAY_THREE_SECONDS);
    if (withFlags || organic) {
      setTimeout(() => {
        checkPopupVisible();
      }, duration);
    } else {
      checkPopupVisible();
    }
  };

  const cancelPopupVisible = url => {
    const curPage = getPageEventName(getPathNoLangOnClient(asPath));
    const _key = getPageEventName(getPathNoLangOnClient(url));
    if (_key !== curPage) {
      cancelledRef.current = true;
    }
  };

  useImperativeHandle(ref, () => {
    return {
      show: handlePopupShow
    };
  });

  useEffect(() => {
    router.events.on('routeChangeStart', cancelPopupVisible);
    return () => {
      router.events.off('routeChangeStart', cancelPopupVisible);
    };
  }, []);

  if (!visible) return null;

  return (
    <>
      {checkSourceOfStudent() ? (
        <DynamicStudentLogin {...rest} visible={visible} onCancel={hide} />
      ) : (
        <DynamicPopupCreateAccount
          {...rest}
          visible={visible}
          hasShowStore={hasShowStore}
          organic={organic}
          onCancel={hide}
        />
      )}
    </>
  );
});

export default DynamicCreateAccount;
